import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, payoffDebts } from "@/lib/finance";
import { fmtUSD, fmtMonths } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

export default function DebtPayoffPage() {
    const [debts, setDebts] = useState([
        { name: "Credit Card A", balance: 6500, apr: 22.9, minPayment: 180 },
        { name: "Credit Card B", balance: 2800, apr: 18.5, minPayment: 90 },
        { name: "Car Loan", balance: 14000, apr: 6.5, minPayment: 320 },
    ]);
    const [extra, setExtra] = useState(300);

    const update = (i, k, v) => setDebts(debts.map((d, j) => (j === i ? { ...d, [k]: k === "name" ? v : toNum(v) } : d)));
    const remove = (i) => setDebts(debts.filter((_, j) => j !== i));
    const add = () => setDebts([...debts, { name: `Debt ${debts.length + 1}`, balance: 1000, apr: 15, minPayment: 50 }]);

    const { avalanche, snowball } = useMemo(() => {
        return {
            avalanche: payoffDebts(debts, toNum(extra), "avalanche"),
            snowball: payoffDebts(debts, toNum(extra), "snowball"),
        };
    }, [debts, extra]);

    return (
        <CalculatorShell
            slug="debt-payoff"
            explainer={
                               <>
                    <h2>How this debt payoff calculator works</h2>
                    <p>
                        A debt payoff calculator shows how long it takes to eliminate your debts and how much
                        total interest you'll pay, given a monthly payment amount. It supports both the
                        avalanche and snowball strategies so you can choose the approach that fits your
                        situation.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Minimum payments:</strong> the baseline — paying only minimums can keep you in debt for years and cost multiples of the original balance.</li>
                        <li><strong>Avalanche method:</strong> directs extra payments to the highest-interest debt first — mathematically optimal, minimizes total interest paid.</li>
                        <li><strong>Snowball method:</strong> directs extra payments to the smallest balance first — produces psychological wins that help sustain momentum.</li>
                        <li><strong>Payoff date:</strong> calculated month by month, showing exactly when each debt reaches zero.</li>
                        <li><strong>Total interest:</strong> accumulates across the full timeline, making the cost of delay concrete and visible.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Even an extra $25 per month shortens your payoff timeline and reduces total interest significantly.</li>
                        <li>List every debt — credit cards, loans, medical bills — to see the complete picture before choosing a strategy.</li>
                        <li>Avoid opening new credit lines while actively paying down debt; new balances reset your progress.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-primary/70 font-medium mb-2">
                        Your debts
                    </div>
                    <div className="space-y-3">
                        {debts.map((d, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`debt-row-${i}`}>
                                <input
                                    value={d.name}
                                    onChange={(e) => update(i, "name", e.target.value)}
                                    placeholder="Debt name"
                                    className="col-span-4 h-11 px-3 rounded-md bg-background border border-border font-sans text-sm focus:outline-none focus:border-accent"
                                />
                                <input
                                    value={d.balance}
                                    type="number"
                                    onChange={(e) => update(i, "balance", e.target.value)}
                                    placeholder="Balance"
                                    className="col-span-3 h-11 px-3 rounded-md bg-background border border-border font-mono tabular text-sm focus:outline-none focus:border-accent"
                                />
                                <input
                                    value={d.apr}
                                    type="number"
                                    step="0.1"
                                    onChange={(e) => update(i, "apr", e.target.value)}
                                    placeholder="APR"
                                    className="col-span-2 h-11 px-3 rounded-md bg-background border border-border font-mono tabular text-sm focus:outline-none focus:border-accent"
                                />
                                <input
                                    value={d.minPayment}
                                    type="number"
                                    onChange={(e) => update(i, "minPayment", e.target.value)}
                                    placeholder="Min pmt"
                                    className="col-span-2 h-11 px-3 rounded-md bg-background border border-border font-mono tabular text-sm focus:outline-none focus:border-accent"
                                />
                                <button
                                    onClick={() => remove(i)}
                                    aria-label="Remove"
                                    className="col-span-1 h-11 grid place-items-center text-muted-foreground hover:text-destructive"
                                    data-testid={`debt-remove-${i}`}
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={add}
                        data-testid="debt-add"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-primary/20 text-primary hover:border-primary/40 text-sm"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add debt
                    </button>
                    <div className="pt-4 border-t border-border">
                        <Field label="Extra monthly payment" prefix="$" value={extra} onChange={setExtra} dataTestId="calc-input-extra" />
                    </div>
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Avalanche — debt-free in"
                        value={avalanche.months}
                        format={(n) => fmtMonths(n)}
                        secondary={[
                            { label: "Total interest", value: avalanche.totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-avalanche-int" },
                        ]}
                    />
                    <div className="bg-card border border-border rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            Snowball strategy
                        </div>
                        <div className="font-mono tabular text-2xl text-primary mt-2" data-testid="calc-result-snowball-months">
                            {fmtMonths(snowball.months)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Interest paid: <span className="font-mono tabular">{fmtUSD(snowball.totalInterest, { decimals: 0 })}</span>
                        </div>
                        <div className="mt-3 text-xs text-accent font-medium" data-testid="calc-result-winner">
                            {avalanche.totalInterest < snowball.totalInterest
                                ? `Avalanche saves ${fmtUSD(snowball.totalInterest - avalanche.totalInterest, { decimals: 0 })}`
                                : "Strategies are tied"}
                        </div>
                    </div>
                </div>
            </div>
        </CalculatorShell>
    );
}
